// screens/appendice/QuestionBrowser.tsx — FP6 Appendice v2 question browser.
//
// Lists the official V/F questions for an argomento (GET /api/argomenti/:id/
// questions), each with the correct answer + the proof ("perché"). Questions are
// grouped per sub-section using the chapter's `matched_principle_ids` as the
// bridge (a question belongs to a sub-section when they share a principle).
// Questions whose principles match no sub-section fall into an "Altre domande"
// group so nothing is hidden. The proof is collapsed by default (tap to reveal)
// so the student can self-test before seeing the answer.

import type { JSX } from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTokens } from '../../state/theme';
import { kbApi, type ArgomentoQuestionDto } from '../../api/kb';
import { QuestionProofCard } from './QuestionProofCard';
import { argomentoHue } from '../../state/appendice-identity';
// Single chapter client after the V28xFP merge: api/appendice.ts (richer types).
import type { AppendiceChapter } from '../../api/appendice';

interface QuestionBrowserProps {
  argomentoId: string;
  /** The loaded chapter (for sub-section grouping). Null → flat list. */
  chapter: AppendiceChapter | null;
}

interface QuestionGroup {
  key: string;
  title: string;
  num?: string;
  questions: ArgomentoQuestionDto[];
}

export function QuestionBrowser({ argomentoId, chapter }: QuestionBrowserProps): JSX.Element {
  const tokens = useTokens();
  const [questions, setQuestions] = useState<ArgomentoQuestionDto[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setQuestions(await kbApi.listArgomentoQuestions(argomentoId));
    } catch {
      setError('Non sono riuscito a caricare le domande di questo argomento. Riprova tra qualche secondo.');
    } finally {
      setLoading(false);
    }
  }, [argomentoId]);

  useEffect(() => { void load(); }, [load]);

  // Build sub-section groups via shared principle ids. A question can match more
  // than one sub-section; we place it in the FIRST matching one (stable order)
  // so it isn't duplicated. Leftovers go to "Altre domande".
  const groups = useMemo<QuestionGroup[]>(() => {
    if (!questions) return [];
    if (!chapter || chapter.sub_sections.length === 0) {
      return questions.length ? [{ key: 'all', title: 'Tutte le domande', questions }] : [];
    }
    const principleToSub = new Map<string, { num: string; title: string; order: number }>();
    chapter.sub_sections.forEach((s, order) => {
      for (const pid of s.matched_principle_ids) {
        // matched_principle_ids may be short ("P1") or full ("arg1_P1"); index both.
        const full = pid.startsWith(`${argomentoId}_`) ? pid : `${argomentoId}_${pid}`;
        if (!principleToSub.has(full)) principleToSub.set(full, { num: s.num, title: s.title, order });
      }
    });

    const bySub = new Map<string, ArgomentoQuestionDto[]>();
    const leftovers: ArgomentoQuestionDto[] = [];
    for (const q of questions) {
      let placed = false;
      for (const pid of q.principle_ids) {
        const sub = principleToSub.get(pid);
        if (sub) {
          const arr = bySub.get(sub.num) ?? [];
          arr.push(q);
          bySub.set(sub.num, arr);
          placed = true;
          break;
        }
      }
      if (!placed) leftovers.push(q);
    }

    const out: QuestionGroup[] = [];
    for (const s of chapter.sub_sections) {
      const qs = bySub.get(s.num);
      if (qs && qs.length) out.push({ key: s.num, title: s.title, num: s.num, questions: qs });
    }
    if (leftovers.length) out.push({ key: 'altre', title: 'Altre domande', questions: leftovers });
    return out;
  }, [questions, chapter, argomentoId]);

  if (loading) {
    return <div data-testid="questions-loading" style={centeredMuted(tokens)}>Caricamento domande…</div>;
  }
  if (error) {
    return (
      <div role="alert" style={{ fontFamily: tokens.fonts.body, fontSize: 13, color: tokens.wrong, padding: '12px 0' }}>
        {error}
      </div>
    );
  }
  if (!questions || questions.length === 0) {
    return <div data-testid="questions-empty" style={centeredMuted(tokens)}>Nessuna domanda ufficiale per questo argomento.</div>;
  }

  const total = questions.length;
  return (
    <div data-testid="question-browser" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontFamily: tokens.fonts.mono, fontSize: 11, color: tokens.textMuted }}>
        {total} {total === 1 ? 'domanda ufficiale' : 'domande ufficiali'}
      </div>
      {groups.map((g, gi) => (
        <QuestionGroupSection key={g.key} group={g} tokens={tokens} defaultOpen={gi === 0} hueColor={argomentoHue(argomentoId).text} />
      ))}
    </div>
  );
}

// V29 S4 — collapsible sub-section groups: arg14 alone carries 423 official
// questions, which rendered as one ~50k-character wall. Groups now open on
// demand (first group open by default); the header keeps the count so nothing
// reads as hidden.
function QuestionGroupSection({
  group: g,
  tokens,
  defaultOpen,
  hueColor,
}: {
  group: QuestionGroup;
  tokens: ReturnType<typeof useTokens>;
  defaultOpen: boolean;
  /** The argomento's identity hue for the card edges (restyle 2026-07-14). */
  hueColor: string;
}): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section data-testid={`qgroup-${g.key}`} style={{ background: tokens.surface, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.md, overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          minHeight: 48,
          padding: '10px 14px',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        {g.num && (
          <span style={{ flexShrink: 0, fontFamily: tokens.fonts.mono, fontSize: 11, fontWeight: 700, color: tokens.textFaint, fontVariantNumeric: 'tabular-nums' }}>{g.num}</span>
        )}
        <span style={{ flex: 1, minWidth: 0, fontFamily: tokens.fonts.body, fontSize: 13.5, fontWeight: 700, color: tokens.text, lineHeight: 1.3 }}>
          {g.title}
        </span>
        <span style={{ flexShrink: 0, fontFamily: tokens.fonts.mono, fontSize: 11, color: tokens.textMuted }}>
          {g.questions.length}
        </span>
        <svg
          width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke={tokens.textFaint} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
          style={{ flexShrink: 0, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.16s ease' }}
          aria-hidden="true"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 10px 12px' }}>
          {g.questions.map((q) => (
            <QuestionProofCard key={q.id} q={q} hueColor={hueColor} />
          ))}
        </div>
      )}
    </section>
  );
}

function centeredMuted(tokens: ReturnType<typeof useTokens>) {
  return { fontFamily: tokens.fonts.body, fontSize: 14, color: tokens.textMuted, padding: '24px 0', textAlign: 'center' as const };
}
